/*
 * Copyright (c) Arbeidskracht Groep B.V. - all rights reserved
 */

import {
	ApiError,
	EnterPasswordForFlexServiceWorkerRegistrationDTO,
	EnterPhoneNumberForFlexServiceWorkerRegistrationDTO,
	EnterPhoneVerificationCodeForFlexServiceWorkerRegistrationDTO,
	FlexServiceWorkerRegistrationPasswordStrengthEvaluationRequestDTO,
	FlexServiceWorkerRegistrationService
} from '../../generated/iam-client';
import {from, Observable, of} from 'rxjs';
import {catchError, map} from 'rxjs/operators';
import {
	iamApiFlexServiceWorkerRegistrationEnterPasswordNotFound,
	iamApiFlexServiceWorkerRegistrationEnterPasswordUnexpectedError,
	iamApiFlexServiceWorkerRegistrationEnterPasswordValidationException,
	iamApiFlexServiceWorkerRegistrationEnterPhoneNumberNotFound,
	iamApiFlexServiceWorkerRegistrationEnterPhoneNumberUnexpectedError,
	iamApiFlexServiceWorkerRegistrationEnterPhoneNumberValidationException,
	iamApiFlexServiceWorkerRegistrationEnterPhoneVerificationCodeNotFound,
	iamApiFlexServiceWorkerRegistrationEnterPhoneVerificationCodeUnexpectedError,
	iamApiFlexServiceWorkerRegistrationEnterPhoneVerificationCodeValidationException,
	iamApiFlexServiceWorkerRegistrationEvaluatePasswordStrengthNotFound,
	iamApiFlexServiceWorkerRegistrationEvaluatePasswordStrengthUnexpectedError,
	iamApiFlexServiceWorkerRegistrationPasswordEntered,
	iamApiFlexServiceWorkerRegistrationPasswordStrengthEvaluated,
	iamApiFlexServiceWorkerRegistrationPhoneNumberEntered,
	iamApiFlexServiceWorkerRegistrationPhoneVerificationCodeEntered,
	iamApiFlexServiceWorkerRegistrationResolvePersonalNameNotFound,
	iamApiFlexServiceWorkerRegistrationResolvePersonalNameRequested,
	iamApiFlexServiceWorkerRegistrationResolvePersonalNameUnexpectedError,
	iamApiFlexServiceWorkerRegistrationStateRetrieved,
	iamApiFlexServiceWorkerRegistrationStopPhoneVerificationNotFound,
	iamApiFlexServiceWorkerRegistrationStopPhoneVerificationRequested,
	iamApiFlexServiceWorkerRegistrationStopPhoneVerificationUnexpectedError,
	iamApiRetrieveFlexServiceWorkerRegistrationStateNotFound,
	iamApiRetrieveFlexServiceWorkerRegistrationStateUnexpectedError
} from './actions';
import {AnyAction} from 'redux';

class FlexServiceWorkerRegistrationResource {

	flexServiceWorkerRegistrationState(flexServiceWorkerRegistrationId: string): Observable<AnyAction> {
		const promise = FlexServiceWorkerRegistrationService.flexServiceWorkerRegistrationState(flexServiceWorkerRegistrationId);
		return from(promise)
			.pipe(
				map((responseBody) => iamApiFlexServiceWorkerRegistrationStateRetrieved({state: responseBody})),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiRetrieveFlexServiceWorkerRegistrationStateNotFound());
						default:
							return of(iamApiRetrieveFlexServiceWorkerRegistrationStateUnexpectedError());
					}
				})
			);
	}

	enterPhoneNumber(
		flexServiceWorkerRegistrationId: string, command: EnterPhoneNumberForFlexServiceWorkerRegistrationDTO
	): Observable<AnyAction> {
		const promise =
			FlexServiceWorkerRegistrationService.enterPhoneNumberForFlexServiceWorkerRegistration(flexServiceWorkerRegistrationId, command);
		return from(promise)
			.pipe(
				map(() => iamApiFlexServiceWorkerRegistrationPhoneNumberEntered()),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiFlexServiceWorkerRegistrationEnterPhoneNumberNotFound());
						case 422:
							return of(iamApiFlexServiceWorkerRegistrationEnterPhoneNumberValidationException(error.body));
						default:
							return of(iamApiFlexServiceWorkerRegistrationEnterPhoneNumberUnexpectedError());
					}
				})
			);
	}

	requestPhoneVerificationStop(flexServiceWorkerRegistrationId: string): Observable<AnyAction> {
		const promise = FlexServiceWorkerRegistrationService
			.requestPhoneVerificationStopForFlexServiceWorkerRegistration(flexServiceWorkerRegistrationId);
		return from(promise)
			.pipe(
				map(() => iamApiFlexServiceWorkerRegistrationStopPhoneVerificationRequested()),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiFlexServiceWorkerRegistrationStopPhoneVerificationNotFound());
						default:
							return of(iamApiFlexServiceWorkerRegistrationStopPhoneVerificationUnexpectedError());
					}
				})
			);
	}

	enterPhoneVerificationCode(
		flexServiceWorkerRegistrationId: string, command: EnterPhoneVerificationCodeForFlexServiceWorkerRegistrationDTO
	): Observable<AnyAction> {
		const promise = FlexServiceWorkerRegistrationService
			.enterPhoneVerificationCodeForFlexServiceWorkerRegistration(flexServiceWorkerRegistrationId, command);
		return from(promise)
			.pipe(
				map(() => iamApiFlexServiceWorkerRegistrationPhoneVerificationCodeEntered()),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiFlexServiceWorkerRegistrationEnterPhoneVerificationCodeNotFound());
						case 422:
							return of(iamApiFlexServiceWorkerRegistrationEnterPhoneVerificationCodeValidationException(error.body));
						default:
							return of(iamApiFlexServiceWorkerRegistrationEnterPhoneVerificationCodeUnexpectedError());
					}
				})
			);
	}

	resolvePersonalName(flexServiceWorkerRegistrationId: string): Observable<AnyAction> {
		const promise = FlexServiceWorkerRegistrationService
			.resolvePersonalNameForFlexServiceWorkerRegistration(flexServiceWorkerRegistrationId);
		return from(promise)
			.pipe(
				map(() => iamApiFlexServiceWorkerRegistrationResolvePersonalNameRequested()),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiFlexServiceWorkerRegistrationResolvePersonalNameNotFound());
						default:
							return of(iamApiFlexServiceWorkerRegistrationResolvePersonalNameUnexpectedError());
					}
				})
			);
	}

	evaluatePasswordStrength(
		flexServiceWorkerRegistrationId: string, evaluationRequest: FlexServiceWorkerRegistrationPasswordStrengthEvaluationRequestDTO
	): Observable<AnyAction> {
		const promise = FlexServiceWorkerRegistrationService
			.evaluatedPasswordStrengthForFlexServiceWorkerRegistration(flexServiceWorkerRegistrationId, evaluationRequest);
		return from(promise)
			.pipe(
				map((responseBody) => iamApiFlexServiceWorkerRegistrationPasswordStrengthEvaluated({passwordStrength: responseBody})),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiFlexServiceWorkerRegistrationEvaluatePasswordStrengthNotFound());
						default:
							return of(iamApiFlexServiceWorkerRegistrationEvaluatePasswordStrengthUnexpectedError());
					}
				})
			);
	}

	enterPassword(
		flexServiceWorkerRegistrationId: string, command: EnterPasswordForFlexServiceWorkerRegistrationDTO
	): Observable<AnyAction> {
		const promise = FlexServiceWorkerRegistrationService
			.enterPasswordForFlexServiceWorkerRegistration(flexServiceWorkerRegistrationId, command);
		return from(promise)
			.pipe(
				map(() => iamApiFlexServiceWorkerRegistrationPasswordEntered()),
				catchError((error: ApiError) => {
					switch (error.status) {
						case 404:
							return of(iamApiFlexServiceWorkerRegistrationEnterPasswordNotFound());
						case 422:
							return of(iamApiFlexServiceWorkerRegistrationEnterPasswordValidationException(error.body));
						default:
							return of(iamApiFlexServiceWorkerRegistrationEnterPasswordUnexpectedError());
					}
				})
			);
	}
}

export default FlexServiceWorkerRegistrationResource;
